import React from 'react';
import { Shield, AlertTriangle, Home } from 'lucide-react';
import { Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';

interface AccessDeniedProps {
  requiredRole?: string;
  message?: string;
}

const AccessDenied: React.FC<AccessDeniedProps> = ({ 
  requiredRole = 'ADMIN', 
  message 
}) => {
  const { user } = useAuth();

  const defaultMessage = `This page requires ${requiredRole} access. Your current role (${user?.role}) does not have sufficient permissions.`;

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col justify-center py-12 sm:px-6 lg:px-8">
      <div className="sm:mx-auto sm:w-full sm:max-w-md">
        <div className="flex justify-center">
          <div className="rounded-full bg-red-100 p-4">
            <Shield className="w-12 h-12 text-red-600" />
          </div>
        </div>
        <h2 className="mt-6 text-center text-3xl font-extrabold text-gray-900">
          Access Denied
        </h2>
      </div>

      <div className="mt-8 sm:mx-auto sm:w-full sm:max-w-md">
        <div className="bg-white py-8 px-4 shadow sm:rounded-lg sm:px-10">
          <div className="flex items-center space-x-3 mb-6">
            <AlertTriangle className="w-6 h-6 text-yellow-500" />
            <h3 className="text-lg font-medium text-gray-900">
              Insufficient Permissions
            </h3>
          </div>
          
          <div className="space-y-4">
            <p className="text-sm text-gray-600">
              {message || defaultMessage}
            </p>
            
            <div className="bg-gray-50 rounded-md p-4">
              <div className="text-sm">
                <p className="font-medium text-gray-900">Your Account Details:</p>
                <p className="text-gray-600">Email: {user?.email}</p>
                <p className="text-gray-600">Name: {user?.name}</p>
                <p className="text-gray-600">Role: {user?.role}</p>
              </div>
            </div>

            <div className="text-sm text-gray-600">
              <p>If you believe you should have access to this page, please contact your system administrator.</p>
            </div>
          </div>

          <div className="mt-6">
            <Link
              to="/app/dashboard"
              className="w-full flex justify-center items-center space-x-2 py-2 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
            >
              <Home className="w-4 h-4" />
              <span>Return to Dashboard</span>
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AccessDenied;